"use strict";

const moment = require("moment");
const _ = require("lodash");

const Issues = require("../igao/issues").Issues;

const IssueExtensions = {
  weight: "narushealth/igao/issues/weight",
  lastEncounterModified:
    "http://narushealth.com/fhir/issue-last-modified-ecnounter",
  carePlanCreationDate:
    "http://narushealth.com/fhir/extensions/CarePlan/created_at",
  urgent: "http://narushealth.com/fhir/extensions/issue-urgent"
};

const ActionExtensions = {
  urgent: "http://narushealth.com/fhir/extensions/action-urgent",
  actionId: "http://narushealth.com/fhir/extensions/action-id",
  statusChanged: "http://narushealth.com/fhir/extensions/status-changed"
};

const getIssueExtension = (issue, url) =>
  _.get(issue, "issue.extension", []).find(
    (i) => _.get(i, "url", null) === url
  );

const getActionExtension = (activity, url) =>
  _.get(activity, "detail.extension", []).find(
    (i) => _.get(i, "url", null) === url
  );

function mapIssueToForm(issue) {
  const lastEncounterModified = getIssueExtension(
    issue,
    IssueExtensions.lastEncounterModified
  );
  const carePlanCreationDate = getIssueExtension(
    issue,
    IssueExtensions.carePlanCreationDate
  );

  const weightExtension = getIssueExtension(issue, IssueExtensions.weight);
  const urgentExtension = getIssueExtension(issue, IssueExtensions.urgent);
  const goalIdentifier = issue.goal.identifier?.find(
    (identifier) => identifier.type?.coding?.[0]?.code === "Narus"
  )?.value;
  const issueTemplateId = issue.issue.identifier?.find(
    (identifier) => identifier.type?.text === "IssueID"
  ).value;
  const itemId = issue.issue.identifier?.find(
    (identifier) => identifier.type?.text === "ItemID"
  )?.value;
  const domainId = issue.issue.identifier?.find(
    (identifier) => identifier.type?.text === "DomainID"
  ).value;
  const domainType = issue.issue.identifier?.find(
    (identifier) => identifier.type?.text === "DomainType"
  ).value;

  const result = {
    patientId: _.get(issue, "issue.subject.reference").split("/")[1],
    id: issue.issue.id,
    issueTemplateId,
    description: issue.issue.description,
    goalId: issue.goal.id,
    status: issue.issue.status,
    trend: _.get(issue, "goal.status"),
    priority: _.get(issue, "goal.priority.coding[0].code"),
    weight: _.get(weightExtension, "valueDecimal", 10),
    comments: _.get(issue, "goal.note"),
    lastEncounterModified: _.get(lastEncounterModified, "valueString", ""),
    carePlanCreationDate: _.get(carePlanCreationDate, "valueInstant", ""),
    urgent: _.get(urgentExtension, "valueBoolean", false),
    actions: issue.issue.activity
      ? mapActivityToActions(issue.issue.activity)
      : [],
    goal: issue.goal.description?.text,
    goalIdentifier,
    itemId,
    domainId,
    domainType,
    context: issue.issue.context,
    support: issue.issue.support,
    nonClinicalIssue: issue.issue.nonClinicalIssue
  };

  const author = _.get(issue, "issue.author[0]");

  if (author && author.reference) {
    result.assignee = {
      id: author.reference.replace("Practitioner/", ""),
      display: author.display
    };
  }
  return result;
}

// This function is called by the mapIgaoToForm function in order to map
// care plan activty to actions for the Igao Page.
// This function is also called by the mapIgaoToForm function in order to map
// care plan activty to actions for the Modify Modal
function mapActivityToActions(activities) {
  if (activities && activities.length) {
    return activities.map((activity) => {
      const urgentExtension = getActionExtension(
        activity,
        ActionExtensions.urgent
      );

      const action = {
        id: activity.detail.code.coding[0].code,
        description: activity.detail.description,
        status: activity.detail.status,
        start: moment(_.get(activity, "detail.scheduledPeriod.start")),
        date: moment(_.get(activity, "detail.scheduledPeriod.end")),
        dueDate: moment(_.get(activity, "detail.scheduledPeriod.end")).format(
          "MM/DD/YYYY"
        ),
        isChecked: true, // mandatory: true
        isExisting: true,
        comments: activity.progress,
        urgent: _.get(urgentExtension, "valueBoolean", false),
        statusChanged: getActionExtension(
          activity,
          ActionExtensions.statusChanged
        )?.valueDateTime,
        actionId: getActionExtension(activity, ActionExtensions.actionId)
          ?.valueString
      };
      const performer = _.get(activity, "detail.performer[0]");
      if (performer && performer.reference) {
        action.assignee = {
          id: performer.reference.replace("Practitioner/", ""),
          display: performer.display
        };
      }

      return action;
    });
  } else if (activities) {
    return activities;
  }
  return [];
}

const mapIssueTemplateToForm = (
  issueTemplate,
  currentUser,
  patientId,
  substitutions
) => {
  const replaceSubstitutions = (text) => {
    if (!substitutions) {
      return text;
    }

    let newText = String(text);
    Object.keys(substitutions).forEach((k) => {
      newText = newText.replace(new RegExp(`{\\s*${k}\\s*}`), substitutions[k]);
    });

    return newText;
  };
  return {
    issueTemplateId: issueTemplate.id,
    description: replaceSubstitutions(issueTemplate.description.issue),
    assignee: currentUser,
    status: "in-progress",
    trend: "planned",
    weight: issueTemplate.priority.value,
    patientId,
    actions: []
  };
};

function mapFormToIssue({ issue, currentUser, encounterId, uid }) {
  const date = new Date();
  const issueTemplate = Issues[issue.issueTemplateId] || {
    goal: {
      description: issue.goal,
      id: issue.goalIdentifier
    },
    domain: {
      id: issue.domainId,
      type: issue.domainType
    }
  };

  const issueUrgent =
    ["not-started", "in-progress"].includes(issue.status) &&
    !_.chain(issue.actions)
      .filter((a) => {
        return ["not-started", "in-progress"].includes(a.status) && a.urgent;
      })
      .isEmpty()
      .value();

  const newIssue = {
    goal: {
      id: issue.goalId,
      description: {
        text: issueTemplate.goal.description
      },
      resourceType: "Goal",
      identifier: [
        {
          use: "official",
          type: { coding: [{ code: "Narus" }] },
          value: issueTemplate.goal.id
        }
      ],
      note: issue.comments || [],
      status: issue.trend ? issue.trend : "planned"
    },
    issue: {
      resourceType: "CarePlan",
      goal: issue.goalId && [{ reference: `Goal/${issue.goalId}` }],
      identifier: [
        {
          use: "official",
          type: {
            text: "IssueID"
          },
          value: issue.issueTemplateId
        },
        {
          use: "official",
          type: {
            text: "DomainID"
          },
          value: issueTemplate.domain.id
        },
        {
          use: "official",
          type: {
            text: "DomainType"
          },
          value: issueTemplate.domain.type
        }
      ],
      id: issue.id,
      subject: { reference: `Patient/${issue.patientId}` },
      description: issue.description,
      status: issue.status,
      activity: mapActionsToActivities({
        actions: issue.actions,
        currentUser,
        uid
      }),
      extension: [
        {
          url: "narushealth/igao/issues/weight",
          valueDecimal: issue.weight
        },
        {
          url: "http://narushealth.com/fhir/issue-last-modified-ecnounter",
          valueString: encounterId || ""
        },
        {
          url: "http://narushealth.com/fhir/extensions/CarePlan/created_at",
          valueInstant: issue.carePlanCreationDate || new Date()
        },
        {
          url: "http://narushealth.com/fhir/extensions/issue-urgent",
          valueBoolean: issueUrgent
        }
      ]
    }
  };

  if (issue.itemId) {
    newIssue.issue.identifier.push({
      use: "official",
      type: {
        text: "ItemID"
      },
      value: issue.itemId
    });
  }

  if (issue.context) {
    newIssue.issue.context = issue.context;
  }

  if (issue.support) {
    newIssue.issue.support = issue.support;
  }

  if (issue.comment) {
    const note = {
      // add to front of list
      authorReference: {
        reference: `Practitioner/${currentUser.id}`,
        display: currentUser.display
      },
      time: date, // When the annotation was made
      text: issue.comment // R!  The annotation  - text content
    };
    if (uid) {
      note.extension = [
        {
          url: "http://narushealth.com/fhir/extensions/note-id",
          valueString: uid()
        }
      ];
    }
    newIssue.goal.note.unshift(note);
  }

  if (issue.assignee) {
    newIssue.issue.author = [
      {
        reference: `Practitioner/${issue.assignee.id}`,
        display: issue.assignee.display
      }
    ];
  }

  if (issue.priority) {
    newIssue.goal.priority = {
      coding: [
        {
          system: "http://hl7.org/fhir/ValueSet/goal-priority",
          code: issue.priority
        }
      ]
    };
  }

  return newIssue;
}

function mapActionToActivity({ action, currentUser, uid }) {
  const date = new Date();

  // let endDate = new Date(date.valueOf() + action.daysDue * 1000 * 60 * 60 * 24);
  if (action.comment) {
    const newComment = {
      // add to front of list
      authorReference: {
        reference: `Practitioner/${currentUser.id}`,
        display: currentUser.display
      },
      time: date, // When the annotation was made
      text: action.comment // R!  The annotation  - text content
    };
    if (uid) {
      newComment.extension = [
        {
          url: "http://narushealth.com/fhir/extensions/note-id",
          valueString: uid()
        }
      ];
    }
    action.comments = action.comments ? action.comments : [];
    action.comments.unshift(newComment);
  }
  if (uid && !action.actionId) {
    action.actionId = uid();
  }

  const result = {
    detail: {
      description: action.description,
      status: action.status || "not-started",
      prohibited: false,
      scheduledPeriod: {
        start: action.start ? new Date(action.start) : date,
        end:
          _.get(action, "dueDate", "").length === 10
            ? new Date(action.dueDate)
            : new Date(
                date.getTime() + (action.dueDays || 1) * 24 * 3600 * 1000
              )
      },
      code: {
        coding: [
          {
            system: "http://narusheath.com/fhir/action-id",
            code: action.id
          }
        ]
      },
      extension: _.compact([
        {
          url: "http://narushealth.com/fhir/extensions/action-urgent",
          valueBoolean: action.urgent
        },
        action.actionId && {
          url: "http://narushealth.com/fhir/extensions/action-id",
          valueString: action.actionId
        },
        {
          url: "http://narushealth.com/fhir/extensions/status-changed",
          valueDateTime: action.statusChanged
        }
      ])
    },
    progress: action.comments
  };

  if (action.assignee) {
    result.detail.performer = [
      {
        reference: `Practitioner/${_.get(action, "assignee.id")}`,
        display: _.get(action, "assignee.display")
      }
    ];
  }

  return result;
}

function mapActionsToActivities({ actions, currentUser, uid }) {
  return actions.map((action) =>
    mapActionToActivity({
      action,
      currentUser,
      uid
    })
  );
}

module.exports = {
  mapIssueToForm,
  mapFormToIssue,
  mapIssueTemplateToForm,
  getIssueExtension,
  IssueExtensions,
  ActionExtensions,
  mapActionToActivity
};
